/**
 * 프로필 데코레이터 캔버스 규격 — "프로필 테두리형 데코레이터 상세 규격 기획서" 기준.
 *
 * 정사각 캔버스에 내접하는 원의 반지름을 100%로 볼 때
 *   A 내부 프로필 영역 70% / B 내부 띠 15% / C 외부 띠 15%
 * 즉 프로필 원의 지름은 캔버스 한 변의 70%다.
 *
 * 에셋 제작 시의 안전 영역(2~5% 내부 여백)은 제작 가이드라인이므로 렌더에는 반영하지 않는다.
 *
 * 실제 에셋 제작 규격이 달라지면 PROFILE_RATIO 만 조정하면 된다.
 */
export const PROFILE_RATIO = 0.7;

/** 프로필 원 지름(px) → 데코 캔버스 한 변(px) */
export const toCanvasSize = (avatarSize: number) => Math.round(avatarSize / PROFILE_RATIO);

/**
 * 확장 캔버스 합성본에서 프로필 원 대비 캔버스가 커지는 배율.
 * 같은 크기로 보이게 하려면 이미지를 이 배율로 키워야 한다.
 */
export const EXPANDED_SCALE = 1 / PROFILE_RATIO;

/**
 * 확장 캔버스가 프로필 원 바깥으로 넘치는 여백(px). 한쪽 기준.
 *
 * 인접 UI 를 가리지 않으려면 이만큼 레이아웃 여백을 확보해야 한다
 * (상세 규격 기획서: "닉네임·팔로우 버튼·코인 버튼 등 인접 UI와 겹치지 않도록 여백 확보").
 */
export const toExpandedOverflow = (avatarSize: number) => Math.round((avatarSize * (EXPANDED_SCALE - 1)) / 2);

/**
 * 목록 행에서 아바타 **왼쪽**에 예약할 여백(px).
 *
 * 오른쪽은 닉네임·버튼이 붙지만 왼쪽은 페이지 가장자리뿐이라 돌출분을 전부 비울 이유가 없다.
 * 전부 비우면 프로필 원이 시안 위치보다 돌출분만큼 안쪽으로 밀려 왼쪽이 허전해 보인다(#2344).
 * 절반만 예약해 데코는 가장자리 쪽으로 더 나가게 하고, 원은 시안 위치에 가깝게 되돌린다.
 */
export const toExpandedLeadingOverflow = (avatarSize: number) => Math.round(toExpandedOverflow(avatarSize) / 2);

/**
 * 확장 캔버스 이미지 자체에 적용할 스타일. emotion 템플릿에 그대로 끼워 쓴다.
 * 레이아웃 점유는 그대로 두고 시각 크기만 키우려고 width 대신 transform 을 쓴다.
 *
 * pointer-events 를 끄는 이유 — 원 밖으로 넘친 데코가 팔로우 버튼 같은 인접 요소를 덮으면
 * 클릭을 가로챈다. 데코는 장식일 뿐이므로 이벤트는 통과시킨다.
 */
export const EXPANDED_AVATAR_IMAGE_CSS = `
  border: none;
  border-radius: 0;
  background-color: transparent;
  object-fit: contain;
  transform: scale(${1 / PROFILE_RATIO});
  pointer-events: none;
`;

/**
 * 확장 캔버스를 감싸는 박스에 적용할 스타일.
 * 원형 클리핑·테두리·배경을 걷어내지 않으면 투명 캔버스 위에 원이나 네모가 드러난다.
 */
export const EXPANDED_AVATAR_BOX_CSS = `
  border: none;
  background-color: transparent;
  overflow: visible;
`;

/* ------------------------------------------------------------------ *
 * 확장 합성본 판별
 *
 * 서버는 확장형 데코와 레거시 데코를 같은 `composedAvatar` 필드로 같은 경로에 내려주기 때문에
 * URL만으로는 구분할 수 없다. 대신 두 이미지는 내용이 다르다.
 *   - 레거시 합성본: 아바타를 캔버스에 꽉 채운 **불투명** 정사각 이미지
 *   - 확장 합성본:   프로필 원 바깥이 **투명**한 캔버스
 * 그래서 이미지 네 모서리의 알파값을 실제로 읽어 판별한다.
 *
 * 판별은 **확장이 증명된 경우에만 true** 다. 로드 실패·CORS 오염·캔버스 미지원 등
 * 확인이 불가능한 모든 경우는 false(= 기존 원형 클리핑)로 떨어진다.
 * 잘못 확장하면 불투명 사진이 네모로 노출되지만, 잘못 클리핑하면 기존 화면과 같아서다.
 * ------------------------------------------------------------------ */

/** 서버 합성본 URL 인지 (원본 아바타는 판별 대상이 아니다) */
const COMPOSED_AVATAR_PATTERN = /composed-avatars?/;

/** 서버에 확장 캔버스 합성본을 요청하는 쿼리 파라미터 */
const EXPANDED_CANVAS_PARAM = ['canvas', 'expanded'] as const;

/** 판별·캐시에 쓸 키 — 쿼리를 뗀 원본 URL */
const toBaseUrl = (url: string) => url.split('?')[0];

/**
 * 확장 캔버스 합성본 URL 로 바꾼다. 기존 쿼리(`?w=` 등)는 유지한다.
 *
 * 서버는 같은 경로에 `?canvas=expanded` 를 붙이면 프로필 원을 캔버스의 70%로 축소 배치하고
 * 바깥을 투명하게 남긴 합성본을 준다. 파라미터가 없으면 기존(불투명·꽉 찬) 합성본이 온다.
 */
export const toExpandedAvatarUrl = (url: string) => {
  const [base, query = ''] = url.split('?');
  const params = new URLSearchParams(query);
  params.set(...EXPANDED_CANVAS_PARAM);
  return `${base}?${params.toString()}`;
};

/** 이미지 URL에 요청 폭(`w`)을 붙인다. 기존 쿼리(`?canvas=expanded` 등)는 유지한다. */
export const withImageWidth = (url: string, width?: number) => {
  if (!width) return url;
  const [base, query = ''] = url.split('?');
  const params = new URLSearchParams(query);
  params.set('w', String(width));
  return `${base}?${params.toString()}`;
};

/**
 * 판별 전용 요청 폭.
 *
 * ⚠ **화면이 쓰지 않는 값이어야 한다.** 자산 서버는 `Vary: Origin` 을 주면서도 CDN 이 캐시하는데,
 * 화면용 `<img>` 는 Origin 없이(= CORS 헤더 없는 응답) 받아 캐시한다. 판별은 `crossOrigin` 으로
 * 받아야 하는데 같은 URL이면 그 캐시된 응답을 재사용해 CORS 가 깨지고 canvas 가 오염된다.
 * 첫 로드는 되고 새로고침하면 판별이 실패하던 원인이 이것이다.
 * 폭을 따로 두면 판별 URL은 항상 CORS 요청으로만 채워져 충돌이 없다.
 */
const PROBE_WIDTH = 64;

/** 판별용 요청 — 알파만 보면 되므로 작은 리사이즈본으로 받는다(알파 보존 확인됨) */
const toProbeUrl = (url: string) => `${toExpandedAvatarUrl(toBaseUrl(url))}&w=${PROBE_WIDTH}`;

/** 0~255. 이보다 작으면 투명으로 본다 (webp 압축 잔차 허용) */
const TRANSPARENT_ALPHA_MAX = 8;
/** 모서리 알파만 보면 되므로 아주 작게 줄여 그린다 */
const PROBE_SIZE = 16;

/** null = 확인 불가(로드 실패·CORS 등). 캐시에 남기지 않고 다음 기회에 다시 시도한다. */
type ProbeResult = boolean | null;

const probeCache = new Map<string, boolean | Promise<boolean>>();

const probeTransparentCorners = (url: string): Promise<ProbeResult> =>
  new Promise((resolve) => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      resolve(null);
      return;
    }

    const image = new Image();
    // 캔버스 오염을 막으려면 CORS 로 받아야 한다. 서버가 허용하지 않으면 getImageData 가 던지고,
    // 아래 catch 에서 null(확인 불가)로 떨어져 기존 원형 렌더가 유지된다.
    image.crossOrigin = 'anonymous';
    image.decoding = 'async';

    image.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = PROBE_SIZE;
        canvas.height = PROBE_SIZE;

        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) {
          resolve(null);
          return;
        }

        context.drawImage(image, 0, 0, PROBE_SIZE, PROBE_SIZE);
        const { data } = context.getImageData(0, 0, PROBE_SIZE, PROBE_SIZE);
        const alphaAt = (x: number, y: number) => data[(y * PROBE_SIZE + x) * 4 + 3];

        const max = PROBE_SIZE - 1;
        const corners = [alphaAt(0, 0), alphaAt(max, 0), alphaAt(0, max), alphaAt(max, max)];
        const isTransparent = corners.every((alpha) => alpha <= TRANSPARENT_ALPHA_MAX);
        resolve(isTransparent);
      } catch {
        // CORS 로 캔버스가 오염되면 픽셀을 읽을 수 없다 → 확인 불가이므로 재시도 여지를 남긴다
        resolve(null);
      }
    };
    image.onerror = () => resolve(null);
    image.src = url;
  });

/**
 * 이미 판별이 끝난 URL이면 그 결과를, 아직이면 undefined 를 즉시 돌려준다.
 * 원본 아바타처럼 판별 자체가 필요 없는 URL은 false 로 확정된다.
 */
export const getExpandedAvatarState = (url?: string): boolean | undefined => {
  if (!url) return false;
  if (!isComposedAvatarUrl(url)) return false;

  const cached = probeCache.get(toBaseUrl(url));
  return typeof cached === 'boolean' ? cached : undefined;
};

/** 서버가 합성한 데코 아바타 URL 인지. 원본 아바타는 확장 대상이 아니다. */
export const isComposedAvatarUrl = (url?: string): boolean => {
  if (!url) return false;

  return COMPOSED_AVATAR_PATTERN.test(toBaseUrl(url));
};

/** 확장 캔버스인지 실제로 검사한다. 같은 URL은 세션 동안 한 번만 검사한다. */
export const resolveExpandedAvatar = (url: string): Promise<boolean> => {
  const key = toBaseUrl(url);

  const cached = probeCache.get(key);
  if (typeof cached === 'boolean') return Promise.resolve(cached);
  if (cached) return cached;

  const pending = probeTransparentCorners(toProbeUrl(key)).then((result) => {
    // 확인 불가(null)는 캐시하지 않는다 — 일시적 실패가 세션 내내 굳어버리는 것을 막는다
    if (result === null) probeCache.delete(key);
    else probeCache.set(key, result);
    return result ?? false;
  });
  probeCache.set(key, pending);

  return pending;
};
